import { useMemo } from 'react';

import { useBillingPeriods } from './useBillingPeriods';

interface Params {
  contractId?: number;
  /** A single selected period; when set it is the only period in view. */
  billingPeriodId?: number;
  /** Report date range (ISO), used when no single period is selected. */
  min?: string;
  max?: string;
  contractCurrency?: string;
  dolarRate?: number;
  enabled?: boolean;
}

export interface PackageAllowance {
  /** Package hours × 3600, summed over every period the current view covers. */
  capSeconds: number;
  /** Package price in the contract's currency, summed over the same periods. */
  capPrice: number;
  /** How many billing periods the current filter covers. */
  periodsCovered: number;
}

const EMPTY: PackageAllowance = { capSeconds: 0, capPrice: 0, periodsCovered: 0 };

/**
 * The package allowance the current report view is entitled to.
 *
 * The "Total Billed" cards used to print a single package (`/ 20h`) no matter how
 * many billing periods were on screen, so an unfiltered contract with seven closed
 * months still claimed a 20-hour allowance and reported everything above it as
 * overtime. The allowance is a **per-period** entitlement: it is renewed on the
 * first day of each period and unused hours are not carried forward, so a view
 * spanning N periods is entitled to the sum of those N periods' packages.
 *
 * Each period is summed by its OWN package — INSTANCE contracts open periods with
 * a per-period tariff, so they are not interchangeable.
 *
 * Defect report AL-2026/08-03-01, D-06.
 */
export function usePackageAllowance({
  contractId,
  billingPeriodId,
  min,
  max,
  contractCurrency,
  dolarRate,
  enabled = true,
}: Params): PackageAllowance {
  const { data } = useBillingPeriods(contractId, enabled);

  return useMemo(() => {
    const periods = data ?? [];
    if (!periods.length) return EMPTY;

    const covered = periods.filter((p) => {
      if (billingPeriodId) return p?.id === billingPeriodId;

      const start = p?.startDate ? new Date(p.startDate).getTime() : null;
      const end = p?.endDate ? new Date(p.endDate).getTime() : Number.POSITIVE_INFINITY;
      if (start === null) return false;

      // No range picked: count every period that has already opened. Future
      // periods carry no entitlement yet, so they must not inflate the cap.
      if (!min && !max) return start <= Date.now();

      const from = min ? new Date(min).getTime() : Number.NEGATIVE_INFINITY;
      const to = max ? new Date(max).getTime() : Number.POSITIVE_INFINITY;
      return end >= from && start <= to;
    });

    return covered.reduce<PackageAllowance>((acc, p) => {
      const pkg = p?.package;
      if (!pkg) return { ...acc, periodsCovered: acc.periodsCovered + 1 };

      // Mirrors TasksService.acceptTask: a USD package on a UZS contract is
      // converted at the contract's rate; every other combination is used as-is.
      const price =
        contractCurrency === 'UZS' && pkg?.curency === 'USD' && dolarRate
          ? Number(pkg.price) * dolarRate
          : Number(pkg.price);

      return {
        capSeconds: acc.capSeconds + Number(pkg.hours ?? 0) * 3600,
        capPrice: acc.capPrice + (Number.isFinite(price) ? price : 0),
        periodsCovered: acc.periodsCovered + 1,
      };
    }, EMPTY);
  }, [data, billingPeriodId, min, max, contractCurrency, dolarRate]);
}
